// ExamNotesPanel — Structured exam preparation with concepts and keywords
// Always shows header, add button and export — no large empty state

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Card from '../ui/Card';
import type { ExamConcept } from '../../types';
import type { ConceptNode } from '../../types/conceptMap';

interface ExamNotesPanelProps {
  examConcepts: ExamConcept[];
  sessionKeywords: string[];
  topicTitle: string;
  onConceptAdd: (concept: Omit<ExamConcept, 'id' | 'createdAt'>) => void;
  onConceptUpdate: (id: string, updates: Partial<ExamConcept>) => void;
  onConceptDelete: (id: string) => void;
  onStartQualification: (concept: ExamConcept) => void;
  onExport: () => void;
  conceptMapNodes?: ConceptNode[];
}

const ExamNotesPanel: React.FC<ExamNotesPanelProps> = ({
  examConcepts,
  sessionKeywords,
  topicTitle: _topicTitle,
  onConceptAdd,
  onConceptUpdate,
  onConceptDelete,
  onStartQualification,
  onExport,
  conceptMapNodes,
}) => {
  const { t } = useTranslation('preparation');

  const [showAddForm, setShowAddForm] = useState(false);
  const [newTerm, setNewTerm] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTerm, setEditTerm] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Merge concept map nodes with exam concepts
  const allConcepts = useMemo(() => {
    if (!conceptMapNodes || conceptMapNodes.length === 0) {
      return examConcepts; // Fallback to manual concepts
    }
    // Map concept map nodes to ExamConcept format
    const fromMap: ExamConcept[] = conceptMapNodes.map(node => {
      const existing = examConcepts.find(
        ec => ec.term.toLowerCase() === node.label.toLowerCase()
      );
      return existing ?? {
        id: `cm-${node.id}`,
        term: node.label,
        description: '',
        qualified: false,
        source: 'concept-map',
        createdAt: new Date().toISOString(),
      };
    });
    // Add any manual concepts not in the map
    const mapTerms = new Set(conceptMapNodes.map(n => n.label.toLowerCase()));
    const manualOnly = examConcepts.filter(ec => !mapTerms.has(ec.term.toLowerCase()));
    return [...fromMap, ...manualOnly];
  }, [conceptMapNodes, examConcepts]);

  // Keywords that aren't already concepts
  const unconvertedKeywords = sessionKeywords.filter(
    kw => !allConcepts.some(c => c.term.toLowerCase() === kw.toLowerCase())
  );

  const handleAddConcept = () => {
    if (!newTerm.trim()) return;
    onConceptAdd({
      term: newTerm.trim(),
      description: newDescription.trim(),
      qualified: false,
      source: 'manual',
    });
    setNewTerm('');
    setNewDescription('');
    setShowAddForm(false);
  };

  const handleConvertKeyword = (keyword: string) => {
    onConceptAdd({
      term: keyword,
      description: '',
      qualified: false,
      source: 'session-keyword',
    });
  };

  const handleStartEdit = (concept: ExamConcept) => {
    setEditingId(concept.id);
    setEditTerm(concept.term);
    setEditDescription(concept.description);
  };

  const handleSaveEdit = (id: string) => {
    onConceptUpdate(id, {
      term: editTerm.trim(),
      description: editDescription.trim(),
    });
    setEditingId(null);
  };

  const hasConcepts = allConcepts.length > 0;
  const hasKeywords = unconvertedKeywords.length > 0;

  return (
    <Card className="min-h-[60vh] overflow-y-auto" padding="none">
      <div className="p-6 space-y-5">
        {/* Header — always visible */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-neutral-dark dark:text-white">
              {t('exam.headerTitle')}
            </h3>
            <p className="text-sm text-neutral-medium mt-1 leading-relaxed">
              {t('exam.headerDescription')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={!hasConcepts}
            className="shrink-0"
            title={!hasConcepts ? t('exam.exportDisabledHint') : undefined}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {t('exam.exportShort')}
          </Button>
        </div>

        {/* Progress indicator — only when concepts exist */}
        {hasConcepts && (
          <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${(allConcepts.filter(c => c.qualified).length / allConcepts.length) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-neutral-medium whitespace-nowrap">
              {allConcepts.filter(c => c.qualified).length}/{allConcepts.length} {t('exam.qualified')}
            </span>
          </div>
        )}

        {/* Session keywords section */}
        {hasKeywords && (
          <div>
            <h4 className="text-xs font-semibold text-neutral-medium uppercase tracking-wide mb-2">
              {t('exam.sessionKeywords')}
            </h4>
            <div className="flex flex-wrap gap-2">
              {unconvertedKeywords.map((keyword, i) => (
                <button
                  key={i}
                  onClick={() => handleConvertKeyword(keyword)}
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                  title={t('exam.convertKeyword')}
                >
                  <span className="text-primary-400 group-hover:text-primary-600 dark:group-hover:text-primary-200 transition-colors">+</span>
                  {keyword}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Concepts list */}
        {hasConcepts && (
          <div>
            <h4 className="text-xs font-semibold text-neutral-medium uppercase tracking-wide mb-2">
              {t('exam.concepts')}
            </h4>
            <div className="space-y-3">
              {allConcepts.map((concept) => (
                <div
                  key={concept.id}
                  className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700"
                >
                  {editingId === concept.id ? (
                    // Edit mode
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editTerm}
                        onChange={(e) => setEditTerm(e.target.value)}
                        className="w-full px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-neutral-dark dark:text-white"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-neutral-dark dark:text-white resize-none"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveEdit(concept.id)} className="text-xs px-3 py-1.5 bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors">
                          {t('exam.saveEdit')}
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-xs px-3 py-1.5 text-neutral-medium hover:text-neutral-dark transition-colors">
                          {t('exam.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-neutral-dark dark:text-white text-sm">
                              {concept.term}
                            </span>
                            {concept.qualified ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                {t('exam.qualifiedBadge')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                                {t('exam.notQualifiedBadge')}
                              </span>
                            )}
                          </div>
                          {concept.description && (
                            <p className="text-sm text-neutral-medium leading-relaxed">
                              {concept.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                        {!concept.qualified && (
                          <button
                            onClick={() => onStartQualification(concept)}
                            className="text-xs px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                          >
                            {t('exam.qualifyWithAnna')}
                          </button>
                        )}
                        <button
                          onClick={() => handleStartEdit(concept)}
                          className="text-xs px-3 py-1.5 text-neutral-medium hover:text-neutral-dark dark:hover:text-gray-300 transition-colors"
                        >
                          {t('exam.edit')}
                        </button>
                        <button
                          onClick={() => onConceptDelete(concept.id)}
                          className="text-xs px-3 py-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors ml-auto"
                        >
                          {t('exam.delete')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty hint — when no concepts and no keywords */}
        {!hasConcepts && !hasKeywords && (
          <div className="text-center py-6 text-neutral-medium">
            <p className="text-sm">{t('exam.emptyHint')}</p>
          </div>
        )}

        {/* Add concept form / button — always visible */}
        {showAddForm ? (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
            <input
              type="text"
              value={newTerm}
              onChange={(e) => setNewTerm(e.target.value)}
              placeholder={t('exam.termPlaceholder')}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-neutral-dark dark:text-white placeholder:text-gray-400"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && newTerm.trim()) handleAddConcept(); }}
            />
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder={t('exam.descriptionPlaceholder')}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-neutral-dark dark:text-white placeholder:text-gray-400 resize-none"
              rows={2}
            />
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={handleAddConcept} disabled={!newTerm.trim()}>
                {t('exam.add')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setShowAddForm(false); setNewTerm(''); setNewDescription(''); }}>
                {t('exam.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-neutral-medium hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors"
          >
            + {t('exam.addConcept')}
          </button>
        )}
      </div>
    </Card>
  );
};

export default ExamNotesPanel;
